import { useState, useEffect, useCallback, useRef } from 'react';
import { Message } from '@/types/chat';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useChatHistory } from '../useChatHistory';
import { useToast } from "@/components/ui/use-toast";

// Key for localStorage
const CHAT_STATE_STORAGE_KEY = 'analystx_chat_state';

/**
 * Hook for managing chat messages state and transitions
 */
export const useChatMessages = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [fadingIn, setFadingIn] = useState(false);
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const [isLoadingChatHistory, setIsLoadingChatHistory] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loadChatHistory, saveChatHistory } = useChatHistory();
  const initializationRef = useRef<boolean>(false);
  const processingChatId = useRef<string | null>(null);
  const { toast } = useToast();

  // Load chat state from localStorage on initial render
  useEffect(() => {
    try {
      const savedState = localStorage.getItem(CHAT_STATE_STORAGE_KEY);
      if (savedState) {
        const { messages: savedMessages, currentChatId: savedChatId } = JSON.parse(savedState);
        if (savedMessages && savedMessages.length > 0) {
          setMessages(savedMessages);
        }
        if (savedChatId) {
          setCurrentChatId(savedChatId);
        }
      }
    } catch (error) {
      console.error("Error loading chat state from localStorage:", error);
    }
  }, []);

  // Save chat state to localStorage whenever it changes
  useEffect(() => {
    if (messages.length > 0 || currentChatId) {
      try {
        localStorage.setItem(CHAT_STATE_STORAGE_KEY, JSON.stringify({
          messages,
          currentChatId
        }));
      } catch (error) {
        console.error("Error saving chat state to localStorage:", error);
      }
    }
  }, [messages, currentChatId]);

  /**
   * Initializes chat messages based on URL parameters or clears them for non-authenticated users
   */
  const initializeChatHistory = useCallback(async (isAuthenticated: boolean | null, chatId?: string | null) => {
    // Skip if not authenticated
    if (isAuthenticated === false) return;
    
    // Get chatId from parameter or URL
    const chatIdToUse = chatId || searchParams.get('chat');
    
    // Skip if we're already processing this chat ID
    if (chatIdToUse === processingChatId.current) return;
    
    processingChatId.current = chatIdToUse;
    setIsTransitioning(true);
    setFadingIn(true);
    
    if (chatIdToUse) {
      console.log("Loading chat history for:", chatIdToUse);
      // Only load history if it's a different chat than what we're currently viewing
      if (chatIdToUse !== currentChatId) {
        setIsLoadingChatHistory(true);
        try {
          const initialMessages = await loadChatHistory(chatIdToUse);
          
          // Check if we got messages back
          if (initialMessages && initialMessages.length > 0) {
            console.log("Successfully loaded chat history:", initialMessages.length, "messages");
            
            // If this is a report, only show the most recent user question and the report
            if (initialMessages.length >= 2 && 
                initialMessages[initialMessages.length - 1].role === 'assistant' && 
                initialMessages[initialMessages.length - 1].content.length > 500) {
              
              // Keep only the user query and the report
              const userQuery = initialMessages[initialMessages.length - 2];
              const report = initialMessages[initialMessages.length - 1];
              
              if (userQuery.role === 'user') {
                setMessages([userQuery, report]);
              } else {
                // Fallback if structure is unexpected
                setMessages(initialMessages);
              }
            } else {
              // If not a report, show all messages
              setMessages(initialMessages);
            }
            
            setCurrentChatId(chatIdToUse);
            
            // Save to localStorage
            localStorage.setItem(CHAT_STATE_STORAGE_KEY, JSON.stringify({
              messages: initialMessages,
              currentChatId: chatIdToUse
            }));
          } else {
            console.warn("No messages found for chat ID:", chatIdToUse);
            toast({
              title: "Chat history not found",
              description: "We couldn't find any messages for this chat.",
              variant: "destructive",
            });
            // If URL has a chat ID but no history was found, stay on the page but clear the messages
            setMessages([]);
            setCurrentChatId(null);
            
            // Update URL without redirecting
            navigate("/chat?empty=true", { replace: true });
          }
        } catch (error) {
          console.error("Error loading chat history:", error);
          toast({
            title: "Error loading chat",
            description: "We couldn't load this chat history. Please try again.",
            variant: "destructive",
          });
          // Clear messages if loading fails
          setMessages([]);
          setCurrentChatId(null);
          
          // Clear localStorage
          localStorage.removeItem(CHAT_STATE_STORAGE_KEY);
          
          // Update URL without redirecting
          navigate("/chat?error=true", { replace: true });
        } finally {
          setIsLoadingChatHistory(false);
        }
      }
    } else if (searchParams.get('new')) {
      // Handle new report request from URL
      console.log("New report requested via URL parameter");
      setMessages([]);
      setCurrentChatId(null);
      localStorage.removeItem(CHAT_STATE_STORAGE_KEY);
    } else {
      // Keep existing messages unless explicitly asked to clear them
      // This ensures we don't lose messages when navigating within the app
      console.log("No chat ID provided, keeping existing messages if any");
    }
    
    setIsTransitioning(false);
    setTimeout(() => {
      setFadingIn(false);
      // Clear the processing flag after animation completes
      processingChatId.current = null;
    }, 300);
    
    // Mark as initialized
    initializationRef.current = true;
  }, [loadChatHistory, searchParams, currentChatId, toast, navigate]);

  /**
   * Saves chat history for a user and updates URL with chat ID
   * Modified to prevent duplicate entries for similar reports
   */
  const saveChatHistoryAndUpdateUrl = useCallback(async (userId: string, userMessage: string, updatedMessages: Message[]) => {
    if (!userId) return null;
    
    try {
      // Check if we have a current chat ID that we should update rather than creating a new one
      if (currentChatId) {
        console.log(`Updating existing chat: ${currentChatId}`);
        // Update existing chat history instead of creating a new one
        await useChatHistory().saveChatHistory(userId, userMessage, updatedMessages, currentChatId);
        
        // Save to localStorage
        localStorage.setItem(CHAT_STATE_STORAGE_KEY, JSON.stringify({
          messages: updatedMessages,
          currentChatId
        }));
        
        return currentChatId;
      }
      
      // If no current chat ID, create a new one
      const chatId = await useChatHistory().saveChatHistory(userId, userMessage, updatedMessages);
      
      if (chatId) {
        // Always navigate to /chat with the chat ID parameter to ensure we stay in the chat UI
        navigate(`/chat?chat=${chatId}`, { replace: true });
        
        setCurrentChatId(chatId);
        
        // Save to localStorage
        localStorage.setItem(CHAT_STATE_STORAGE_KEY, JSON.stringify({
          messages: updatedMessages,
          currentChatId: chatId
        }));
      }
      return chatId;
    } catch (saveError) {
      console.error('Error saving chat history:', saveError);
      return null;
    }
  }, [navigate, currentChatId]);

  return {
    messages,
    setMessages,
    isTransitioning,
    setIsTransitioning,
    fadingIn,
    setFadingIn,
    currentChatId,
    isLoadingChatHistory,
    initializeChatHistory,
    saveChatHistoryAndUpdateUrl
  };
};
